const initialized = new WeakSet();

export default function initSideStripe(gallery) {
    if (initialized.has(gallery)) return;

    const layers = Array.from(gallery.children).filter(element => element.matches('figure.wp-block-image'));
    const images = layers.map(layer => layer.querySelector('img'));
    if (layers.length !== 2 || images.some(image => !image)) return;
    initialized.add(gallery);

    const comparison = document.createElement('div');
    comparison.className = 'comparison';
    gallery.insertBefore(comparison, layers[0]);

    layers.forEach((layer, index) => {
        // Replace Gutenberg's column styling with full-size comparison layers.
        layer.classList.remove('wp-block-image');
        layer.classList.add('image', index === 0 ? 'before' : 'after');

        if (!layer.querySelector('figcaption')) {
            const caption = document.createElement('figcaption');
            caption.textContent = index === 0 ? 'Before' : 'After';
            layer.appendChild(caption);
        }

        comparison.appendChild(layer);
    });

    const updateRatio = () => {
        for (const image of images) {
            const width = Number(image.getAttribute('width')) || image.naturalWidth;
            const height = Number(image.getAttribute('height')) || image.naturalHeight;
            if (width > 0 && height > 0) {
                comparison.style.setProperty('--image-ratio', `${width} / ${height}`);
                break;
            }
        }
    };

    images.forEach(image => image.addEventListener('load', updateRatio, { once: true }));
    updateRatio();

    const divider = document.createElement('span');
    divider.className = 'divider';
    divider.setAttribute('aria-hidden', 'true');

    const handle = document.createElement('span');
    handle.className = 'handle';
    handle.setAttribute('aria-hidden', 'true');

    const control = document.createElement('input');
    control.className = 'control';
    control.type = 'range';
    control.min = '0';
    control.max = '100';
    control.step = '1';
    control.value = '50';
    control.setAttribute('aria-label', 'Before and after image comparison');

    comparison.append(divider, handle, control);
    gallery.classList.add('ready');

    const updatePosition = (value) => {
        const position = Math.min(100, Math.max(0, value));
        const width = comparison.getBoundingClientRect().width;
        const inset = Math.min(handle.getBoundingClientRect().width / 2, width / 2);
        const percentage = width > 0
            ? Math.round((inset + (width - inset * 2) * position / 100) / width * 100)
            : 50;

        comparison.style.setProperty('--progress', String(position / 100));
        control.value = String(Math.round(position));
        control.setAttribute('aria-valuetext', `Before: ${percentage}%; After: ${100 - percentage}%`);
    };

    const followPointer = (event) => {
        const rect = comparison.getBoundingClientRect();
        const inset = handle.getBoundingClientRect().width / 2;
        const travel = rect.width - inset * 2;
        if (travel > 0) updatePosition((event.clientX - rect.left - inset) / travel * 100);
    };

    let pointerId = null;

    control.addEventListener('input', () => updatePosition(Number(control.value)));
    control.addEventListener('keydown', () => comparison.classList.remove('is-pointer'));
    control.addEventListener('blur', () => comparison.classList.remove('is-pointer'));
    comparison.addEventListener('pointerdown', (event) => {
        if (!event.isPrimary || event.button !== 0) return;
        event.preventDefault();
        pointerId = event.pointerId;
        comparison.classList.add('is-pointer');
        control.focus({ preventScroll: true });
        comparison.setPointerCapture(pointerId);
        followPointer(event);
    });

    comparison.addEventListener('pointermove', (event) => {
        if (event.pointerId === pointerId) {
            followPointer(event);
        }
    });

    const release = (event) => {
        if (event.pointerId !== pointerId) return;
        if (comparison.hasPointerCapture(pointerId)) comparison.releasePointerCapture(pointerId);
        pointerId = null;
    };

    comparison.addEventListener('pointerup', release);
    comparison.addEventListener('pointercancel', release);
    comparison.addEventListener('lostpointercapture', release);

    const observer = new ResizeObserver(() => updatePosition(Number(control.value)));
    observer.observe(comparison);
    observer.observe(handle);
    updatePosition(50);
}
